import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowDownRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowDownRounded';
import KeyboardDoubleArrowUpRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowUpRounded';
import LocalPrintshopRoundedIcon from '@mui/icons-material/LocalPrintshopRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { FC, Fragment, KeyboardEvent, MouseEvent, ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { buildNoPlanBadgeRowIds } from 'src/components/dispatch/noPlanVehicle';
import { resolveRowVehicleOverride, ShiftTruckRowInfo } from 'src/components/dispatch/shiftTruckRowInfo';
import { resolveSwapTargets } from 'src/components/dispatch/swapTargets';
import { STICKY_HEADER_ATTR } from 'src/components/dispatch/useScrollToRow';
import { buildVehicleSortableGroups, truckOrderFromKeys } from 'src/components/dispatch/vehicleAssignment/vehicleSortableGroups';
import {
  DRAG_HANDLE_WIDTH, VEHICLE_CARD_HEIGHT,
  VEHICLE_LIST_HEADER_ROW1_HEIGHT, VEHICLE_LIST_HEADER_ROW2_HEIGHT,
} from 'src/components/dispatch/vehicleCardStyles';
import {
  ALL_ROTATION_FILTERS, buildVehicleFilterLabel, hasGroupNarrowing, hasRotationNarrowing,
  RotationFilterValue, VehicleFilterMode,
} from 'src/components/dispatch/vehicleFilter';
import { ExpandedRowVM, ExpandedStopVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { ExpandedRowPanel } from 'src/components/dispatch/vehicleRowExpand/ExpandedRowPanel';
import { reorderByKey } from 'src/components/dispatch/vehicleRowExpand/reorderByKey';
import TruckDisplayStatusMenu from 'src/components/TruckDisplayStatusMenu';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';

import { buildDisplayRotationNumbers, RotationRow, VehicleRowGroup } from './adapter';
import { VehicleTariffTotals } from './buildGrossProfitByTruckId';
import { DisplayMetric } from './DisplayMetricToggle';
import { VehicleInfoView } from './VehicleInfoView';

import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

// 静的な modifier。レンダリングのたびに新しい配列を生成しないようコンポーネント外で定義する。
const dndModifiers = [restrictToVerticalAxis];

type BaseProps = {
  /** truck 並べ替え可否 (= 単日表示 startOn === endOn のとき true) */
  canSortTruckDisplayOrder: boolean;
  /** mutation 進行中フラグ。in-flight 中は次の drag をブロックする */
  isMutatingTruckOrder: boolean;
  /** drag 完了時に Presenter で楽観的更新 + サーバ保存を行うためのコールバック */
  onTruckOrderChange: (truckIds: number[]) => void;
  rowGroups: VehicleRowGroup[];
  isLoading: boolean;
  displayMetrics: Set<DisplayMetric>;
  /**
   * truckId → 粗利 (円)。呼び出し側の Presenter が算出して渡す。
   * optional にすると両画面の配線漏れが tsc もテストもすり抜けるため必須にしている。
   * 粗利を出さない画面は空 Map を渡す。
   */
  grossProfitByTruckId: ReadonlyMap<number, VehicleTariffTotals>;
  /**
   * 積載以外の制約違反（勤務時間の超過など）。勤務 (shift_truck) 単位の判定なので、
   * その勤務の先頭の回転にだけ出す（全行に出すと同じ違反が回転の数だけ並ぶ）。
   */
  constraintAlertsByShiftTruckId?: ReadonlyMap<number, readonly string[]>;
  /**
   * 自動配車で新しく案件が入った勤務。shift_truck.id → 案件 ID。
   * 車両単位にすると、1 台に複数のドライバーが乗る勤務で同じ車両の全行が光る。
   * /map だけが渡す（カード配車は渡さないので現状の見た目のまま）
   */
  newlyAssignedByShiftTruck?: ReadonlyMap<number, ReadonlySet<number>>;
  selectedRowIds: Set<string>;
  allSelected: boolean;
  /** 車両リストの絞り込み（配車状況・回転・選択中）。1 つだけ選べる */
  truckDisplayStatus: VehicleFilterMode;
  onTruckDisplayStatusChange: (mode: VehicleFilterMode) => void;
  /** チェック済みの回転。既定は全部（絞っていない状態） */
  rotations: readonly RotationFilterValue[];
  onRotationsChange: (next: RotationFilterValue[]) => void;
  /** 制約違反のある車両だけに絞るか。既定はチェックなし */
  constraintViolationOnly: boolean;
  onConstraintViolationOnlyChange: (next: boolean) => void;
  hasSelection: boolean;
  /** id=0「すべて表示する」を先頭に含むグループ全体。空ならグループ UI を出さない */
  truckGroupEntitiesWithAll: PlanningsGroupEntity[];
  /**
   * driverId → ドライバー名。1 台に複数のドライバーが乗る勤務で行ごとの名前を出すのに使う。
   * 未指定なら従来どおり車両単位の Vehicle.driverName を出す。
   */
  driverNameById?: ReadonlyMap<number, string>;
  /**
   * shift_truck.id → 勤務時間・車番。ドライバー名と同じく、1 台に複数のドライバーが
   * 乗る勤務では車両単位の代表値が使えない。未指定なら従来どおり車両単位の値を出す。
   */
  shiftTruckRowInfoById?: ReadonlyMap<number, ShiftTruckRowInfo>;
  /** 現在チェック済みのグループ index 群 */
  selectedTruckGroupIndices: number[];
  onSelectedTruckGroupIndicesChange: (next: number[]) => void;
  onRowToggle: (rowId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  /** 選択中の行の案件をまとめて未割当に戻す。未指定ならボタン非表示（readOnly とは独立） */
  onUnassignSelected?: () => void;
  /**
   * 選択中の 2 車両の配送計画を入れ替える（V3Plans の swapRequest 相当）。
   * 未指定ならボタン非表示。別車両 2 台を選んだときだけ出る。
   */
  onSwapSelected?: (fromDeliveryId: number, toDeliveryId: number) => void;
  /** 未指定ならボタン非表示（readOnly とは独立） */
  onAddVehicle?: () => void;
  /** 行の DOM を rowId で登録する。地図から選んだ行までスクロールするのに使う */
  registerRow?: (rowId: string, element: HTMLElement | null) => void;
  /**
   * 「計画無」バッジの × 。未指定なら × を出さない。
   * shiftTruckId はその行の勤務。1 台に複数ドライバーが乗る勤務で対象を決めるのに使う
   */
  /** 勤務ごと削除する。対象は押したカードの勤務 (shift_truck.id) */
  onDeleteShift?: (shiftTruckId: number | null) => void;
  /** 指定時は × を disabled にし、素の title 属性で理由を出す */
  deleteShiftDisabledReason?: string;
  /** 回転は勤務 (delivery) の中の概念。追加先・削除先は勤務で決める */
  onAddRotation: (deliveryId: number) => void;
  onDeleteRotation: (deliveryId: number, rotationNumber: number) => void;
  /** 指定時は回転削除ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #2。VehicleInfoView への中継）。 */
  deleteRoundDisabledReason?: string;
  /**
   * 車両名 / ドライバー名 / 車番 クリック時のハンドラ。Presenter 側でメニューを開いて
   * mutation を発火する。対象は勤務 (shift_truck.id)。1 台に複数の勤務が乗るので
   * truckId では対象を決められない。
   */
  onTruckEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  onDriverEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  onPlateNumberEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  /** 帳票出力 (V3Plans の SelectPrintPresenter と同じダイアログ) を開く */
  onOpenPrint?: () => void;
  /**
   * 配車指示メールを送る。未指定ならボタンを出さない
   * （ドライバーアプリのライセンスが無い会社では handler ごと渡さない）。
   */
  onSendOperationMail?: () => void;
  /**
   * 配送状況の文言（「N分前に更新あり」/「配送状況 MM/dd HH:mm」）。
   * 未指定なら配送状況を出さない。
   */
  operationStatusText?: string;
  /**
   * 配送状況の再取得。**新しい更新を見つけたときだけ**渡す。
   * 平時に押す用の無いボタンを常駐させない（旧地図配車と同じ規則）。
   */
  onRefreshOperationStatuses?: () => void;
  /** 指定時はメールボタンを disabled にし、素の title 属性で理由を出す */
  sendOperationMailDisabledReason?: string;
  /** false で回転追加/削除ボタンを隠す。default true（convenience 現状維持）。readOnly とは独立。 */
  rotationEditable?: boolean;
  /** 確定済み shift_truck(delivery) id 集合。各行の確定バッジ表示に使う。readOnly とは独立。 */
  fixedShiftTruckIds?: Set<number>;
  /** 「配車確定/確定解除」ボタン押下。未指定ならボタン非表示。readOnly とは独立。 */
  onToggleFixedSelected?: () => void;
  /** 確定ボタンのラベル。undefined のときボタン非表示（有効な確定対象が 0 件のとき Presenter が undefined を渡す）。 */
  fixedToggleLabel?: string;
  /** 確定 mutation 実行中フラグ。 */
  isTogglingFixed?: boolean;
  /** 指定時は確定ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #1）。 */
  fixedToggleDisabledReason?: string;
  /** truckId から車両カラー (hex) を引く。undefined 返却 or prop 未指定なら色バー非表示。 */
  vehicleColorFor?: (truckId: number) => string | undefined;
  /** 色バー クリック時のハンドラ。Presenter 側で色ピッカーを開く。 */
  onVehicleColorClick?: (truckId: number, anchor: HTMLElement) => void;
  /** 指定時は色バーを disabled にし、素の title 属性で理由を出す（Task 10 #7。VehicleInfoView への中継）。 */
  colorDisabledReason?: string;
  /**
   * 停車地行のクリック。未指定なら展開行はクリックできない(P2b の onShowDetail と同じ規則)。
   * 展開が有効でも任意なので判別共用体には入れない。
   */
  onSelectStop?: (card: DispatchCard, vehicle: Vehicle, dispatchOrderIndex: number) => void;
  /**
   * 回転を並べ替えたときに呼ぶ。未指定なら並べ替えできない（ハンドルも出さない）。
   * 渡すのは同じ車両の「動かした回転」と「落とした先の回転」の rotationNumber。
   */
  /** 回転は勤務 (delivery) の中の並び。対象は掴んだカードの勤務 (shift_truck.id) */
  onReorderRotations?: (shiftTruckId: number | null, fromRotationNumber: number, toRotationNumber: number) => void;
  /**
   * 展開行の停車地を並べ替えたときに呼ぶ。未指定なら並べ替えできない。
   * 渡すのは並べ替え後の停車地全体で、保存する id 列の組み立ては呼び出し側が行う。
   */
  onReorderStops?: (row: RotationRow, stops: ExpandedStopVM[]) => void;
  /** 指定時は並べ替えハンドルを disabled にし、素の title 属性で理由を出す */
  reorderDisabledReason?: string;
  /**
   * GA の screen_name。**必須**。既定値を持たせると誤ラベルの GA が静かに飛ぶ
   * (P2b で全境界について必須と決めた契約)。ConvenienceDispatchUnassignedList:102 と同じ prop 名。
   */
  screenName: string;
};

/**
 * rowExpandable が true のとき expandedRowIds / onToggleRowExpanded / expandedRowVMByRowId が
 * 必須という関係を型に落とす。false 側にも同名を never で置くのは、このコンポーネントが
 * 引数で props を一括分割代入しているため。false 側に同名が無い共用体だと分割代入の時点で
 * TS2339 になる(実測確認済み)。never を置いても rowExpandable だけ渡した配線漏れは TS2322 で検出できる。
 */
type RowExpandProps =
  | {
      rowExpandable?: false;
      expandedRowIds?: never;
      onToggleRowExpanded?: never;
      onToggleAllExpanded?: never;
      allRowsExpanded?: never;
      expandedRowVMByRowId?: never;
    }
  | {
      rowExpandable: true;
      expandedRowIds: ReadonlySet<string>;
      onToggleRowExpanded: (rowId: string) => void;
      /** ヘッダの「すべて展開 / すべて閉じる」。対象行の解決は Presenter 側 */
      onToggleAllExpanded: () => void;
      /** 表示中の行がすべて展開済みか。ボタンの向き（展開 / 閉じる）を決める */
      allRowsExpanded: boolean;
      /** rowId → 展開行 VM。地図マーカーと同じ番号にするため Presenter が 1 回だけ作った Map を渡す */
      expandedRowVMByRowId: ReadonlyMap<string, ExpandedRowVM>;
    };

type Props = BaseProps & RowExpandProps;

type SortableVehicleGroupProps = {
  /** 並べ替えの識別子。勤務単位 (`shift-{shiftTruckId}`) なので string */
  id: string;
  disabled: boolean;
  /** ハンドルはカード内に置くため、要素を受け取って好きな位置に描く */
  children: (dragHandle: ReactNode) => ReactNode;
};

/** ハンドル列の幅。掴めないときも同じ幅を空けて行頭を揃える */


/**
 * truck 単位の vertical sortable item。
 *
 * 掴めるのは左端のハンドルだけ。カード全体を掴めるようにすると、行選択・回転追加・
 * 色ピッカーなど中の操作のたびにドラッグが始まってしまう。
 */
const SortableVehicleGroup: FC<SortableVehicleGroupProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });

  // ハンドルはカードの中（VehicleInfoView の左端）に置くので、要素を作って子へ渡す。
  // ここで描くとカードの外に出てしまい、行の背景と分離する。
  const handle = <VehicleDragHandle disabled={disabled} attributes={attributes} listeners={listeners} />;

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={{ display: 'flex', alignItems: 'stretch' }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{children(handle)}</Box>
    </Box>
  );
};

type DragHandleProps = {
  disabled?: boolean;
  /** useSortable の戻り値をそのまま渡す。掴めないときは undefined */
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
};

/**
 * 並び替えハンドル。並び替えできない状況（複数日表示・並び替え不可の車両）でも
 * 場所は空けておく。消すと行頭の位置が車両ごとに変わって画面が跳ねる。
 */
const VehicleDragHandle: FC<DragHandleProps> = ({ disabled, attributes, listeners }) => (
  <Box
    component="button"
    type="button"
    aria-label="車両の並び替え"
    disabled={disabled}
    {...(disabled ? {} : attributes)}
    {...(disabled ? {} : listeners)}
    sx={{
      flexShrink: 0,
      width: DRAG_HANDLE_WIDTH,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      p: 0,
      bgcolor: 'transparent',
      color: 'hsl(var(--muted-foreground))',
      opacity: disabled ? 0.25 : 0.6,
      cursor: disabled ? 'default' : 'grab',
      '&:active': disabled ? undefined : { cursor: 'grabbing' },
      '&:hover': disabled ? undefined : { opacity: 1 },
    }}
  >
    <DragIndicatorIcon sx={{ fontSize: 14 }} />
  </Box>
);

/**
 * 回転行の DnD 有効範囲。並べ替えない画面では素通しし、DOM を増やさない
 * (convenience 画面の既存構造を変えないため)。
 *
 * Why 入れ子の DndContext: 外側は車両の並び替えを持っている。同じ context に
 * 回転行を混ぜると、車両 id と行 id が同じ items に並んで落とし先が入り乱れる。
 */
const RotationSortableScope: FC<{
  enabled: boolean;
  sensors: ReturnType<typeof useSensors>;
  rowIds: string[];
  onDragEnd: (event: DragEndEvent) => void;
  children: ReactNode;
}> = ({ enabled, sensors, rowIds, onDragEnd, children }) => {
  if (!enabled) return <>{children}</>;
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={dndModifiers}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

type SortableRotationRowProps = {
  id: string;
  /** 掴めるか。1 行目は車両ハンドルが場所を使っているので掴めない（落とし先にはなる） */
  draggable: boolean;
  disabledReason?: string;
  children: (dragHandle: ReactNode) => ReactNode;
};

/**
 * 回転単位の vertical sortable item。
 *
 * Why 1 行目を掴めなくする: 行頭のハンドル位置は車両の並び替えが先に使っている。
 * 同じ場所に 2 つ置くと「どちらが動くのか」が見て分からない。1 行目は落とし先には
 * なるので、2 行目を 1 行目の上へ落とせば先頭にできる。
 */
const SortableRotationRow: FC<SortableRotationRowProps> = ({
  id, draggable, disabledReason, children,
}) => {
  const disabled = !draggable || !!disabledReason;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    // 掴めない行も落とし先には要る
    disabled: { draggable: disabled, droppable: false },
  });

  // 掴めない行にはハンドルを描かない（車両ハンドルが同じ場所に居る）
  const handle = draggable ? (
    <Box
      component="button"
      type="button"
      aria-label="回転の並べ替え"
      disabled={disabled}
      // disabled 要素は MUI Tooltip の hover を拾えないので素の title で出す
      title={disabledReason}
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
      sx={{
        flexShrink: 0,
        width: DRAG_HANDLE_WIDTH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        p: 0,
        bgcolor: 'transparent',
        color: 'hsl(var(--muted-foreground))',
        opacity: disabled ? 0.25 : 0.6,
        cursor: disabled ? 'default' : 'grab',
        '&:active': disabled ? undefined : { cursor: 'grabbing' },
        '&:hover': disabled ? undefined : { opacity: 1 },
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 14 }} />
    </Box>
  ) : undefined;

  return (
    <Box ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      {children(handle)}
    </Box>
  );
};

const ConvenienceDispatchVehicleList: FC<Props> = memo(({
  canSortTruckDisplayOrder,
  isMutatingTruckOrder,
  onTruckOrderChange,
  rowGroups,
  isLoading,
  displayMetrics,
  grossProfitByTruckId,
  constraintAlertsByShiftTruckId,
  newlyAssignedByShiftTruck,
  selectedRowIds,
  allSelected,
  truckDisplayStatus,
  onTruckDisplayStatusChange,
  rotations,
  onRotationsChange,
  constraintViolationOnly,
  onConstraintViolationOnlyChange,
  hasSelection,
  truckGroupEntitiesWithAll,
  driverNameById,
  shiftTruckRowInfoById,
  selectedTruckGroupIndices,
  onSelectedTruckGroupIndicesChange,
  onRowToggle,
  onSelectAll,
  onDeselectAll,
  onUnassignSelected,
  onSwapSelected,
  onAddVehicle,
  registerRow,
  onDeleteShift,
  deleteShiftDisabledReason,
  onAddRotation,
  onDeleteRotation,
  deleteRoundDisabledReason,
  onTruckEdit,
  onDriverEdit,
  onPlateNumberEdit,
  onOpenPrint,
  onSendOperationMail,
  sendOperationMailDisabledReason,
  operationStatusText,
  onRefreshOperationStatuses,
  rotationEditable = true,
  fixedShiftTruckIds,
  onToggleFixedSelected,
  fixedToggleLabel,
  isTogglingFixed = false,
  fixedToggleDisabledReason,
  vehicleColorFor,
  onVehicleColorClick,
  colorDisabledReason,
  rowExpandable = false,
  expandedRowIds,
  onToggleRowExpanded,
  onToggleAllExpanded,
  allRowsExpanded = false,
  expandedRowVMByRowId,
  onSelectStop,
  onReorderRotations,
  onReorderStops,
  reorderDisabledReason,
  screenName,
}) => {
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // 車両表示順の DnD。実際の楽観的更新 + サーバ保存は Presenter の onTruckOrderChange に委譲する
  // (Presenter 側で local state を持ち、ボード描画にも反映するため)。
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // 回転を並べ替えたときの見た目。保存 (debounce 2 秒) が返るまでこちらを描く。
  // vehicle.id → 並べ替え後の行。rowGroups が入れ替わったらサーバの並びに戻す
  const [rotationPreview, setRotationPreview] = useState<ReadonlyMap<string, RotationRow[]>>(new Map());
  useEffect(() => setRotationPreview(new Map()), [rowGroups]);

  // 並べ替えの識別子は勤務単位。グループは勤務ごとに分かれるので、truckId を使うと
  // 1 台に 2 つの勤務が乗るとき React key と dnd-kit の id が重複する
  const sortableGroups = useMemo(() => buildVehicleSortableGroups(rowGroups), [rowGroups]);
  const sortableKeys = useMemo(() => sortableGroups.map((g) => g.key), [sortableGroups]);

  // 入れ替えの可否と対象は resolveSwapTargets に閉じ込める。
  // null なら条件を満たさないのでボタンごと出さない
  const swapTargets = useMemo(
    () => resolveSwapTargets(selectedRowIds, rowGroups),
    [selectedRowIds, rowGroups],
  );

  const handleVehicleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!canSortTruckDisplayOrder || isMutatingTruckOrder) return;

    const oldIndex = sortableKeys.indexOf(String(active.id));
    const newIndex = sortableKeys.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    // 保存する並び順は車両単位。同じ車両の勤務が 2 つ並ぶので重複を落とす
    onTruckOrderChange(truckOrderFromKeys(sortableGroups, arrayMove([...sortableKeys], oldIndex, newIndex)));
  }, [canSortTruckDisplayOrder, isMutatingTruckOrder, onTruckOrderChange, sortableGroups, sortableKeys]);

  // フィルタボタンのラベル: ステータスが「すべて表示」以外 > 部分選択中のグループ名 > "すべて"
  const isFiltered = truckDisplayStatus !== 'すべて表示'
    || hasGroupNarrowing(selectedTruckGroupIndices)
    || hasRotationNarrowing(rotations)
    || constraintViolationOnly;
  const filterLabel = buildVehicleFilterLabel(
    truckDisplayStatus,
    selectedTruckGroupIndices,
    truckGroupEntitiesWithAll,
    { rotations, constraintViolationOnly },
  );
  // × もメニューの「フィルターをクリア」と同じく全条件を既定へ戻す
  const clearFilters = () => {
    onTruckDisplayStatusChange('すべて表示');
    onSelectedTruckGroupIndicesChange(truckGroupEntitiesWithAll.map((_g, i) => i));
    onRotationsChange([...ALL_ROTATION_FILTERS]);
    onConstraintViolationOnlyChange(false);
  };

  if (isLoading) {
    return (
      <Stack spacing={1} sx={{ p: 1 }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} variant="rectangular" height={72} />
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      {/* 固定ヘッダ (空状態でも常に表示) */}
      <Box
        // 地図から車両を選んだときのスクロール位置計算がこの目印でヘッダ高さを測る
        // (useScrollToRow.ts)。外すと選んだ行がヘッダの裏に潜る
        {...{ [STICKY_HEADER_ATTR]: '' }}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          borderBottom: 1,
          borderColor: 'divider',
          // 不透明にする。半透明だとスクロール時に下の車両カードが透けて、
          // フィルターや全選択が読みにくくなる
          bgcolor: 'hsl(var(--secondary))',
        }}
      >
        {/* Row 1: フィルタ + 車両追加。高さはレーンの列番号ヘッダと共有する定数から取る */}
        <Box sx={{ display: 'flex', height: VEHICLE_LIST_HEADER_ROW1_HEIGHT, alignItems: 'center', justifyContent: 'space-between', px: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {/* 絞り込み中は塗りにして、× をその場に出す。絞り込んでいることに気づかず
                「台数が合わない」と誤解されるのを防ぐ（サンプル準拠） */}
            <Button
              ref={filterButtonRef}
              size="small"
              variant={isFiltered ? 'contained' : 'outlined'}
              color={isFiltered ? 'primary' : 'inherit'}
              startIcon={<FilterListIcon sx={{ fontSize: 12 }} />}
              endIcon={isFiltered ? (
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  aria-label="フィルターをクリア"
                  onClick={(e: MouseEvent<HTMLElement>) => {
                    // 親の onClick（メニューを開く）を止める
                    e.stopPropagation();
                    clearFilters();
                  }}
                  onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    e.stopPropagation();
                    clearFilters();
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: 11,
                    lineHeight: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                  }}
                >
                  ×
                </Box>
              ) : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: screenName,
                  button_name: '車両表示ステータス フィルタ',
                });
                setFilterMenuOpen(true);
              }}
              sx={{ height: 28, px: 1, fontSize: 12, textTransform: 'none', minWidth: 0 }}
            >
              {filterLabel}
            </Button>
            <TruckDisplayStatusMenu
              anchorEl={filterButtonRef.current}
              open={filterMenuOpen}
              onClose={() => setFilterMenuOpen(false)}
              mode={truckDisplayStatus}
              onModeChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '車両表示ステータス',
                  label: next,
                });
                onTruckDisplayStatusChange(next);
              }}
              hasSelection={hasSelection}
              groupEntities={truckGroupEntitiesWithAll}
              selectedGroupIndices={selectedTruckGroupIndices}
              onSelectedGroupIndicesChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '車両グループ選択',
                  label: next.join(','),
                });
                onSelectedTruckGroupIndicesChange(next);
              }}
              rotations={rotations}
              onRotationsChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '回転で絞込',
                  label: next.join(','),
                });
                onRotationsChange(next);
              }}
              constraintViolationOnly={constraintViolationOnly}
              onConstraintViolationOnlyChange={(next) => {
                ReactGA.event('select', {
                  screen_name: screenName,
                  button_name: '制約違反のある車両のみ',
                  label: next ? 'ON' : 'OFF',
                });
                onConstraintViolationOnlyChange(next);
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* readOnly ではなく handler の有無で決める（配車リセット・確定ボタンと同じ分離） */}
            {onAddVehicle && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '車両追加',
                  });
                  onAddVehicle();
                }}
                sx={{ height: 28, px: 1, fontSize: 12, textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap' }}
              >
                車両追加
              </Button>
            )}
            {/* 配送状況。文言は常に出し、再取得ボタンは新しい更新があるときだけ */}
            {operationStatusText && (
              <Typography
                variant="caption"
                sx={{ fontSize: 10, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {operationStatusText}
              </Typography>
            )}
            {onRefreshOperationStatuses && (
              <Tooltip title="配送状況を再取得" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    ReactGA.event('click', {
                      screen_name: screenName,
                      button_name: '配送状況 再取得',
                    });
                    onRefreshOperationStatuses();
                  }}
                  aria-label="配送状況を再取得"
                  sx={{ p: 0.5, color: 'text.primary' }}
                >
                  <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* 帳票出力の左。送る対象＝選択した車両がこのリストにあるので、
                地図のツールバーではなくここに置く */}
            {onSendOperationMail && (
              <Tooltip title={sendOperationMailDisabledReason || '配車指示メールを送る'} arrow>
                {/* disabled な button は hover を拾わないので span で包む(MUI の作法) */}
                <span>
                  <IconButton
                    size="small"
                    disabled={!!sendOperationMailDisabledReason}
                    title={sendOperationMailDisabledReason}
                    onClick={() => {
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '配車指示メール',
                      });
                      onSendOperationMail();
                    }}
                    aria-label="配車指示メールを送る"
                    sx={{ p: 0.5, color: 'text.primary' }}
                  >
                    <MailOutlineRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onOpenPrint && (
              <Tooltip title="配送指示書を出力する" arrow>
                <IconButton
                  size="small"
                  onClick={() => {
                    ReactGA.event('click', {
                      screen_name: screenName,
                      button_name: '帳票出力',
                    });
                    onOpenPrint();
                  }}
                  aria-label="帳票出力"
                  sx={{ p: 0.5, color: 'text.primary' }}
                >
                  <LocalPrintshopRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Row 2: すべて展開 + 全選択 + 選択件数 */}
        <Box sx={{ display: 'flex', height: VEHICLE_LIST_HEADER_ROW2_HEIGHT, alignItems: 'center', gap: 0.75, borderTop: '1px solid hsl(var(--border) / 0.4)', px: 1.5 }}>
          {rowExpandable && onToggleAllExpanded && (
            <Tooltip title={allRowsExpanded ? 'すべて閉じる' : 'すべて展開'} placement="top" arrow>
              <IconButton
                size="small"
                aria-label={allRowsExpanded ? 'すべて閉じる' : 'すべて展開'}
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: screenName,
                    button_name: '車両 すべて展開',
                    label: allRowsExpanded ? 'OFF' : 'ON',
                  });
                  onToggleAllExpanded();
                }}
                sx={{ width: 24, height: 24 }}
              >
                {allRowsExpanded
                  ? <KeyboardDoubleArrowUpRoundedIcon sx={{ fontSize: 16, color: 'text.primary' }} />
                  : <KeyboardDoubleArrowDownRoundedIcon sx={{ fontSize: 16, color: 'text.primary' }} />}
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="text"
            startIcon={allSelected
              ? <CheckBoxOutlinedIcon sx={{ fontSize: 12, color: 'primary.main' }} />
              : <CheckBoxOutlineBlankOutlinedIcon sx={{ fontSize: 12 }} />}
            onClick={() => {
              ReactGA.event('click', {
                screen_name: screenName,
                button_name: '車両 全選択',
                label: allSelected ? 'OFF' : 'ON',
              });
              if (allSelected) onDeselectAll(); else onSelectAll();
            }}
            sx={{ height: 24, px: 0.75, fontSize: 10, textTransform: 'none', minWidth: 0, color: 'text.primary' }}
          >
            {allSelected ? '全解除' : '全選択'}
          </Button>
          {selectedRowIds.size > 0 && (
            <>
              <Box
                component="span"
                sx={{
                  borderRadius: '9999px',
                  bgcolor: 'hsl(var(--primary) / 0.1)',
                  px: 1,
                  py: '2px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'hsl(var(--primary))',
                }}
              >
                {selectedRowIds.size}件選択中
              </Box>
              {/* readOnly ではなく handler の有無で決める。/map は readOnly だが
                  配車リセットは使える（確定ボタンと同じ分離の仕方） */}
              {onUnassignSelected && (
                <>
                  <Box sx={{ mx: 0.5, height: 16, width: '1px', bgcolor: 'hsl(var(--border))' }} />
                  {/* 幅の限られたツールバーではラベルが場所を食うので、サンプル
                      (MapPage.tsx:1362-1375) と同じくアイコンだけにする */}
                  <Tooltip title="選択した車両の配送をすべて未割当に戻す" placement="top" arrow>
                    <IconButton
                      size="small"
                      aria-label="選択した車両の配送を未割当に戻す"
                      onClick={() => {
                        ReactGA.event('click', {
                          screen_name: screenName,
                          button_name: '未割当に戻す',
                        });
                        onUnassignSelected();
                      }}
                      sx={{ width: 24, height: 24 }}
                    >
                      <RestartAltRoundedIcon sx={{ fontSize: 14, color: 'error.main' }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {/* 入れ替えは別車両 2 台のときだけ。条件は resolveSwapTargets に閉じ込め、
                  対象を解決できないときはボタンごと出さない（同 :1376-1399） */}
              {onSwapSelected && swapTargets && (
                <Tooltip title="選択した2車両の配送計画を入れ替える" placement="top" arrow>
                  <IconButton
                    size="small"
                    aria-label="選択した2車両の配送を入れ替える"
                    onClick={() => {
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '配送計画の入れ替え',
                      });
                      onSwapSelected(swapTargets[0], swapTargets[1]);
                    }}
                    sx={{ width: 24, height: 24 }}
                  >
                    <SwapHorizRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {/* ここから右。全選択〜入れ替えは左に固め、確定だけ右へ逃がす
              (サンプル MapPage.tsx:1400) */}
          <Box data-testid="toolbar-spacer" sx={{ flex: 1 }} />
          {onToggleFixedSelected && fixedToggleLabel && (
            <Button
              size="small"
              variant="outlined"
              disabled={isTogglingFixed || !!fixedToggleDisabledReason}
              title={fixedToggleDisabledReason}
              onClick={() => { onToggleFixedSelected(); }}
              sx={{ height: 24, px: 1, fontSize: 11, textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap' }}
            >
              {fixedToggleLabel}
            </Button>
          )}
        </Box>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleVehicleDragEnd}
        modifiers={dndModifiers}
      >
        <SortableContext items={sortableKeys} strategy={verticalListSortingStrategy}>
          {rowGroups.map(({ vehicle, rows: sourceRows, shiftTruckId: groupShiftTruckId }, groupIndex) => {
            const vehicleEnabled = true; // delivery がある vehicle のみ表示されるため常に true
            const isLastGroup = groupIndex === rowGroups.length - 1;
            const truckId = sortableGroups[groupIndex].truckId;
            // 勤務 (delivery) ごとにグループが分かれるので、同じ車両のグループが複数並ぶ。
            // 粗利は車両合計なので最初のグループにだけ出す（全部に出すと同じ金額が
            // 何度も並び、勤務ごとの値と誤読される）
            const isFirstGroupOfTruck = rowGroups.findIndex((g) => g.vehicle.id === vehicle.id) === groupIndex;
            // 保存が返るまでの見た目。debounce で 2 秒待つので、これが無いと掴んだ行が元に戻る
            const previewKey = groupShiftTruckId != null ? `shift-${groupShiftTruckId}` : vehicle.id;
            const rows = rotationPreview.get(previewKey) ?? sourceRows;
            // 回転バッジの番号。並べ替えプレビュー中の並びで数える
            const displayRotationNumbers = buildDisplayRotationNumbers(rows);
            // 「計画無」は勤務 (shift_truck) 単位。× が勤務ごと消す操作なので回転単位では判定せず、
            // かつ車両単位にもしない（1 台に複数のドライバーが乗る勤務では、配車のある人と
            // 無い人が同じ車両に並ぶ）
            const noPlanBadgeRowIds = buildNoPlanBadgeRowIds(rows);

            // 回転が 1 行だけの車両は並べ替えても何も変わらない
            const rotationSortable = !!onReorderRotations && truckId != null && rows.length > 1;
            const handleRotationDragEnd = (event: DragEndEvent) => {
              const next = reorderByKey(rows, (it) => it.rowId, event.active.id, event.over?.id);
              if (!next || !onReorderRotations || truckId == null) return;
              const from = rows.find((it) => it.rowId === event.active.id);
              const to = rows.find((it) => it.rowId === event.over?.id);
              setRotationPreview((prev) => new Map(prev).set(previewKey, next));
              onReorderRotations(groupShiftTruckId, from.rotationNumber, to.rotationNumber);
            };

            // ハンドルはカード内に置くので、外から要素を受け取って各行へ渡す
            const groupContent = (dragHandle: ReactNode) => (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    bgcolor: 'hsl(var(--secondary) / 0.2)',
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <RotationSortableScope
                    enabled={rotationSortable}
                    sensors={sensors}
                    rowIds={rows.map((it) => it.rowId)}
                    onDragEnd={handleRotationDragEnd}
                  >
                  {rows.map((row, rowIndex) => {
                    // 回転の追加・削除の対象になる勤務。「回転を追加」で足した行は
                    // deliveryId が負のダミーなので、追加元 (extraOfDeliveryId) を使う
                    const rowDeliveryId = row.source.extraOfDeliveryId ?? row.source.deliveryId;
                    const groupPosition = rows.length === 1
                      ? 'single'
                      : rowIndex === 0
                        ? 'start'
                        : rowIndex === rows.length - 1
                          ? 'end'
                          : 'middle';
                    const rowRadius = groupPosition === 'single'
                      ? '12px'
                      : groupPosition === 'start'
                        ? '12px 12px 0 0'
                        : groupPosition === 'end'
                          ? '0 0 12px 12px'
                          : '0';
                    const isSelected = selectedRowIds.has(row.rowId);
                    const isNewlyAssigned = row.shiftTruckId != null
                      && !!newlyAssignedByShiftTruck?.get(row.shiftTruckId)?.size;
                    // 空回転(再積み込み拠点だけの placeholder 行)は展開しても中身が無い。
                    // トグルは全行に出す（無いと行頭が揃わない）が、中身が無ければ
                    // パネルは描かずアイコンだけ変える（サンプルと同じ）
                    const expandable = !!rowExpandable;
                    const expanded = expandable && !!expandedRowIds?.has(row.rowId);

                    // 既存の onClick / onKeyDown をそのまま括り出す(挙動は変えない)
                    const handleSelect = () => {
                      ReactGA.event('click', {
                        screen_name: screenName,
                        button_name: '車両行 選択',
                        label: isSelected ? 'OFF' : 'ON',
                      });
                      onRowToggle(row.rowId);
                    };
                    const handleSelectKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
                      // 内側の button (ハンドル・展開・車両名など) での Enter/Space は
                      // そのボタン自身の click になる。ここで拾うと行選択まで二重に走る。
                      // stopPropagation では止まらない (P3-a で実測) ので発生元で弾く。
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect();
                      }
                    };
                    // 既存の sx をそのまま括り出す。flex 系は足さない(false 枝の見た目を変えないため)
                    const selectionSx = {
                      height: VEHICLE_CARD_HEIGHT,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      borderRadius: rowRadius,
                      // 新規割当の色はここに置かない。車両カードがこの Box と同じ大きさで
                      // 重なるため、ここの背景は半透明のカード越しにしか出ず判別できない
                      // (実測: カードの地はほぼ白)。ハイライトは VehicleInfoView 側で当てる
                      bgcolor: isSelected ? 'hsl(var(--primary) / 0.05)' : 'hsl(var(--secondary) / 0.1)',
                      ...(isSelected
                        ? { boxShadow: 'inset 0 0 0 2px hsl(var(--primary))' }
                        : { '&:hover': { bgcolor: 'hsl(var(--secondary) / 0.2)' } }),
                    };
                    // 展開トグルはカード内（色バーの右）に置く。カードの外に出すと
                    // 行の背景と分離して「どの車両のものか」が曖昧になる。
                    // Enter の二重発火は選択面側の handleSelectKeyDown で防ぐ。
                    const expandToggle = expandable ? (
                      <Box
                        component="button"
                        type="button"
                        aria-label={expanded ? '折りたたむ' : '展開する'}
                        aria-expanded={expanded}
                        onClick={(e: MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                          ReactGA.event('click', {
                            screen_name: screenName,
                            button_name: '車両行 展開',
                            label: expanded ? 'OFF' : 'ON',
                          });
                          onToggleRowExpanded(row.rowId);
                        }}
                        sx={{
                          flexShrink: 0,
                          width: 20,
                          alignSelf: 'stretch',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                          bgcolor: 'transparent',
                          cursor: 'pointer',
                          color: 'hsl(var(--muted-foreground))',
                          p: 0,
                        }}
                      >
                        {expanded
                          ? <ExpandMoreIcon sx={{ fontSize: 18 }} />
                          : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                      </Box>
                    ) : undefined;
                    // 回転ハンドルは行ごとに違うので、要素を受け取って行を組む
                    const rowContent = (rotationHandle?: ReactNode) => {
                    const vehicleInfo = (
                      <VehicleInfoView
                        vehicle={vehicle}
                        // 1 台に複数のドライバーが同じ時間帯で乗る勤務があるので、車両単位の
                        // 代表 1 人ではなく行の乗務者を出す。引けないときは従来どおり
                        // vehicle.driverName に落ちる（VehicleInfoView 側の ?? で処理）
                        driverName={row.driverId == null ? undefined : driverNameById?.get(row.driverId)}
                        // 勤務時間・車番も shift_truck ごと。引けなければ車両単位の値に落ちる
                        {...resolveRowVehicleOverride(
                          row.shiftTruckId == null ? undefined : shiftTruckRowInfoById?.get(row.shiftTruckId),
                        )}
                        cards={row.cards}
                        screenName={screenName}
                        dragHandle={rowIndex === 0 ? dragHandle : rotationHandle}
                        expandToggle={expandToggle}
                        noPlan={noPlanBadgeRowIds.has(row.rowId)}
                        onDeleteShift={onDeleteShift && row.truckId != null
                          ? () => onDeleteShift(groupShiftTruckId)
                          : undefined}
                        deleteShiftDisabledReason={deleteShiftDisabledReason}
                        // バッジ表示は同じ勤務 (shift_truck) の中での行順 (1..N) で連番にする。
                        // row.rotationNumber は (a) 「回転追加」で extraRotations に積まれた仮想行
                        // が server 側 cycle と独立した値を保持する、(b) FR placeholder を
                        // hiddenPlaceholderRotations で隠した時にギャップが残る、という二つの理由で
                        // 連番にならないことがある。row.rotationNumber は rowId / 削除コールバック /
                        // overrideRowCards / auto-dispatch の cycle として引き続き使うので変えず、
                        // 表示専用の番号を渡す。
                        //
                        // truck 内の通し番号にしないのは、1 台に複数のドライバーが同じ時間帯で乗る
                        // 勤務があるため。別の勤務が並んでいるだけなのに 2 回転目・3 回転目と出る
                        // （/plans はこの状況を回転扱いしない）。
                        rotationNumber={displayRotationNumbers[rowIndex]}
                        // 車両合計なので 1 回転目の行にだけ渡す。全行に出すと
                        // 同じ数字が並び「回転ごとの値」と誤読される。
                        tariffTotals={rowIndex === 0 && isFirstGroupOfTruck
                          ? grossProfitByTruckId.get(row.truckId) ?? null : null}
                        constraintAlerts={rowIndex === 0 && groupShiftTruckId != null
                          ? constraintAlertsByShiftTruckId?.get(groupShiftTruckId) : undefined}
                        // Why not 選択中は出さない: 自動配車は車両を選択してから実行するため、
                        // 対象車両は結果を見る時点でも選択されたまま。抑制するとハイライトが
                        // ほとんど出ない。選択は枠 (行の Box の inset shadow) で示し分ける
                        highlightNewlyAssigned={isNewlyAssigned}
                        enabled={vehicleEnabled}
                        displayMetrics={displayMetrics}
                        canAddRound={rowIndex === rows.length - 1}
                        canDeleteRound={rows.length > 1}
                        onAddRound={rotationEditable ? () => onAddRotation(rowDeliveryId) : undefined}
                        onDeleteRound={rotationEditable
                          ? () => onDeleteRotation(rowDeliveryId, row.rotationNumber) : undefined}
                        deleteRoundDisabledReason={deleteRoundDisabledReason}
                        onTruckClick={onTruckEdit ? (anchor) => onTruckEdit(groupShiftTruckId, anchor) : undefined}
                        onDriverClick={onDriverEdit ? (anchor) => onDriverEdit(groupShiftTruckId, anchor) : undefined}
                        onPlateNumberClick={onPlateNumberEdit
                          ? (anchor) => onPlateNumberEdit(groupShiftTruckId, anchor) : undefined}
                        rotationEditable={rotationEditable}
                        fixed={!!fixedShiftTruckIds && !row.source.isPlaceholderRow && fixedShiftTruckIds.has(row.source.deliveryId)}
                        barColor={vehicleColorFor?.(row.truckId)}
                        onColorBarClick={onVehicleColorClick ? (anchor) => onVehicleColorClick(row.truckId, anchor) : undefined}
                        colorDisabledReason={colorDisabledReason}
                      />
                    );

                    if (!expandable) {
                      // convenience はここを通る。いまのトップレベル要素をそのまま返す
                      // (DOM の階層も sx も変えない)
                      return (
                        <Box
                          key={`${row.rowId}-info`}
                          ref={registerRow ? (el: HTMLElement | null) => registerRow(row.rowId, el) : undefined}
                          role="button"
                          tabIndex={0}
                          onClick={handleSelect}
                          onKeyDown={handleSelectKeyDown}
                          sx={selectionSx}
                          data-testid={row.rowId}
                        >
                          {vehicleInfo}
                        </Box>
                      );
                    }

                    // VM は Presenter が rowId ごとに 1 回だけ組んだものを引くだけ(地図マーカーと
                    // 番号を一致させるため、ここでは組み立てない。buildExpandedRowVMByRowId 参照)。
                    const expandedVM = expanded ? expandedRowVMByRowId?.get(row.rowId) ?? null : null;

                    return (
                      <Box
                        key={`${row.rowId}-info`}
                        ref={registerRow ? (el: HTMLElement | null) => registerRow(row.rowId, el) : undefined}
                      >
                        {/* ハンドル・展開トグルはカード (vehicleInfo) の中。
                            Enter の二重発火は handleSelectKeyDown が currentTarget を
                            見て防ぐ (内側の button の keydown はここまで上がってくる) */}
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={handleSelect}
                          onKeyDown={handleSelectKeyDown}
                          sx={{ ...selectionSx, display: 'flex', minWidth: 0 }}
                          data-testid={row.rowId}
                        >
                          {vehicleInfo}
                        </Box>
                        {/* 72px の選択行の兄弟。内側に入れると overflow: hidden で切られる */}
                        {expandedVM && (
                          <ExpandedRowPanel
                            vm={expandedVM}
                            cards={row.cards}
                            onSelectCard={onSelectStop
                              ? (card, dispatchOrderIndex) => onSelectStop(card, vehicle, dispatchOrderIndex)
                              : undefined}
                            onReorderStops={onReorderStops ? (stops) => onReorderStops(row, stops) : undefined}
                            reorderDisabledReason={reorderDisabledReason}
                            newlyAssignedOrderIds={row.shiftTruckId == null ? undefined : newlyAssignedByShiftTruck?.get(row.shiftTruckId)}
                          />
                        )}
                        {/* 計画が無い行は開いても見せるものが無い。サンプルもトグルの
                            アイコンだけが変わり、パネルは出ない */}
                      </Box>
                    );
                    };

                    // 並べ替えない画面では sortable にせず、今までの DOM のまま返す
                    if (!rotationSortable) return <Fragment key={`${row.rowId}-row`}>{rowContent()}</Fragment>;
                    return (
                      <SortableRotationRow
                        key={`${row.rowId}-row`}
                        id={row.rowId}
                        draggable={rowIndex > 0}
                        disabledReason={reorderDisabledReason}
                      >
                        {rowContent}
                      </SortableRotationRow>
                    );
                  })}
                  </RotationSortableScope>
                </Box>
                {!isLastGroup && <Box sx={{ mx: 1, borderBottom: '1px solid hsl(var(--border) / 0.4)' }} />}
              </>
            );

            // truckId が null の (= 不正な vehicle.id) 場合は sortable 対象外。
            // ハンドルの場所だけは空けて、他の車両と行頭を揃える
            if (truckId == null) {
              return (
                <Box key={`${sortableGroups[groupIndex].key}-info`} sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>{groupContent(<VehicleDragHandle disabled />)}</Box>
                </Box>
              );
            }
            return (
              <SortableVehicleGroup
                key={`${sortableGroups[groupIndex].key}-info`}
                id={sortableGroups[groupIndex].key}
                disabled={!canSortTruckDisplayOrder || isMutatingTruckOrder}
              >
                {groupContent}
              </SortableVehicleGroup>
            );
          })}
        </SortableContext>
      </DndContext>
    </Box>
  );
});

export default ConvenienceDispatchVehicleList;
